import { create } from "zustand";
import { listForgePages, type NodePage } from "@/services/nodePageService";
import type { GetToken } from "@/services/canvasService";

export type PageCat = "question" | "info" | "deep-dive" | "critical";

// How the pages panel is presented (per design mock). drawer = bottom sheet,
// panel = right side sheet, glass = floating translucent card. Persisted so the
// user's choice survives reloads.
export type PanelTreatment = "drawer" | "panel" | "glass";
const TREATMENT_KEY = "kf:panelTreatment";

function loadTreatment(): PanelTreatment {
  try {
    const v = localStorage.getItem(TREATMENT_KEY);
    if (v === "drawer" || v === "panel" || v === "glass") return v;
  } catch {
    /* SSR / storage disabled — fall through to default */
  }
  return "drawer";
}

export interface NodePageSummary {
  id: string;
  title: string;
  category: PageCat;
}

export interface FocusModePage {
  pageId: string;
  nodeId: string;
  nodeLabel: string;
  initialTitle: string;
  initialCategory: PageCat;
  initialContent: string | null;  // null = fetch content inside focus mode
  initialMode?: "view" | "edit";  // defaults to "edit"
}

interface NodePageDrawerState {
  nodeId: string | null;
  nodeLabel: string;
  initialPageId: string | null;
  pagesByNode: Record<string, NodePage[]>;
  prefetching: boolean;
  focusMode: FocusModePage | null;
  treatment: PanelTreatment;
  setTreatment: (t: PanelTreatment) => void;
  openDrawer: (nodeId: string, nodeLabel: string, initialPageId?: string) => void;
  closeDrawer: () => void;
  setPages: (nodeId: string, pages: NodePage[]) => void;
  upsertPage: (nodeId: string, page: NodePage) => void;
  removePage: (nodeId: string, pageId: string) => void;
  prefetchForge: (forgeId: string, getToken: GetToken) => Promise<void>;
  openFocusMode: (page: FocusModePage) => void;
  closeFocusMode: () => void;
}

export const useNodePageStore = create<NodePageDrawerState>((set) => ({
  nodeId: null,
  nodeLabel: "",
  initialPageId: null,
  pagesByNode: {},
  prefetching: false,
  focusMode: null,
  treatment: loadTreatment(),
  setTreatment: (t) => {
    try { localStorage.setItem(TREATMENT_KEY, t); } catch { /* storage disabled */ }
    set({ treatment: t });
  },
  openDrawer: (nodeId, nodeLabel, initialPageId = undefined) =>
    set({ nodeId, nodeLabel, initialPageId: initialPageId ?? null }),
  closeDrawer: () => set({ nodeId: null, nodeLabel: "", initialPageId: null }),
  setPages: (nodeId, pages) =>
    set((s) => ({ pagesByNode: { ...s.pagesByNode, [nodeId]: pages } })),
  upsertPage: (nodeId, page) =>
    set((s) => {
      const prev = s.pagesByNode[nodeId] ?? [];
      const exists = prev.some((p) => p.id === page.id);
      return {
        pagesByNode: {
          ...s.pagesByNode,
          [nodeId]: exists
            ? prev.map((p) => (p.id === page.id ? page : p))
            : [...prev, page],
        },
      };
    }),
  removePage: (nodeId, pageId) =>
    set((s) => ({
      pagesByNode: {
        ...s.pagesByNode,
        [nodeId]: (s.pagesByNode[nodeId] ?? []).filter((p) => p.id !== pageId),
      },
    })),
  prefetchForge: async (forgeId, getToken) => {
    set({ prefetching: true });
    try {
      const pages = await listForgePages(getToken, forgeId);
      const grouped: Record<string, NodePage[]> = {};
      for (const p of pages) {
        if (!grouped[p.node_id]) grouped[p.node_id] = [];
        grouped[p.node_id].push(p);
      }
      set((state) => ({
        pagesByNode: { ...state.pagesByNode, ...grouped },
        prefetching: false,
      }));
    } catch {
      // Badge counts already came inline with the canvas — a failed prefetch
      // only costs the drawer its instant-open, so there is nothing to report.
      set({ prefetching: false });
    }
  },
  openFocusMode: (page) => set({ focusMode: page }),
  closeFocusMode: () => set({ focusMode: null }),
}));
